import React, { useRef, useCallback, useState, useEffect } from 'react';

import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { Button, Row, Col } from 'react-bootstrap';
import { format, parseISO } from 'date-fns';
import { FaReply } from 'react-icons/fa';

import { useToast } from '../../../hooks/toast';
import { Clinica } from '../../../hooks/auth';

import Loading from '../../../components/Loading';
import EditorDocumento from '../../../components/EditorDocumento';
import {
  htmlDecode,
  optionsDadosCartaoModelo,
  optionsMedicoModelo,
  optionsPacienteModelo,
  optionsClinicaModelo,
  optionsChipModelo,
  optionsChipCancelaModelo,
  TIPO_CONTRATO,
  formataCNPJ,
  formataCep,
  formataTelefone,
  optionsTratamentoModelo,
  optionsProfissionalModelo,
  optionsResponsavelModelo,
  optionsPacoteModelo,
} from '../../../utils/funcoes';
import api from '../../../services/api';
import getResponseErrors from '../../../utils/getResponseErrors';
import getValidationErrors from '../../../utils/getValidationErrors';

export interface IContrato {
  ConCodigo: number;
  ConUsuCodigo: number;
  ConTexto: string;
  ConTitulo: string;
  ConTipo: number;
  ConOrigem: number;
  ConPermiteEdicao: boolean;
  ConDataAlteracao: Date;
  ConDataAlteracaoFormatado: string;
  dataAlteracaoFormatada: string;
  usuarioEditor: {
    UsuCodigo: number;
    UsuNome: string;
  };
}

interface IContratoEClinica {
  contrato: IContrato;
  clinica: Clinica;
}

type Props = {
  tipo:
    | 'NOVO_CARTAO'
    | 'CANCELA_CARTAO'
    | 'RENOVA_CARTAO'
    | 'CANCELA_CARTAO_SEM_FIDELIDADE'
    | 'MEDICO'
    | 'TRATAMENTO'
    | 'NOVO_CHIP'
    | 'CANCELA_CHIP';
  origem: number;
  handleVoltarLista(atualiza: boolean): void;
};

const ContratoCadastro: React.FC<Props> = ({
  tipo,
  origem,
  handleVoltarLista,
}) => {
  const { addToast } = useToast();
  const [conTipo, _] = useState<number>(TIPO_CONTRATO[tipo]);
  const [contrato, setContrato] = useState<IContratoEClinica | undefined>(
    undefined,
  );

  const formRefModelo = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(false);
  const [modelos, setModelos] = useState<
    | {
        text: string;
        value: string;
        label: string;
      }[]
    | undefined
  >(undefined);

  useEffect(() => {
    async function loadDados(): Promise<void> {
      setLoading(true);
      try {
        const response = await api.get<IContratoEClinica>(
          `/contratos/${conTipo}?origem=${origem}`,
        );

        const clinicaFormatada = {
          ...response.data.clinica,
          CliCNPJ: formataCNPJ(response.data.clinica.CliCNPJ),
          CliCEP: formataCep(response.data.clinica.CliCEP),
          CliEndereco: `${response.data.clinica.CliEndereco}, ${
            response.data.clinica.CliNumero
          } ${response.data.clinica.CliComplemento}, ${
            response.data.clinica.CliBairro
          }, ${response.data.clinica.CliCidade}/${
            response.data.clinica.CliUF
          } - CEP ${formataCep(response.data.clinica.CliCEP)}`,
          CliTelefone: formataTelefone(response.data.clinica.CliTelefone),
          CliTelefone2: formataTelefone(response.data.clinica.CliTelefone2),
        };

        const dadosContratoFormatado: IContrato = {
          ...response.data.contrato,
          dataAlteracaoFormatada: format(
            parseISO(response.data.contrato.ConDataAlteracao.toString()),
            "dd/MM/yyyy 'as' HH:mm",
          ),
        };

        setContrato({
          contrato: dadosContratoFormatado,
          clinica: clinicaFormatada,
        });
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível carregar dados do contrato',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    }

    if (conTipo === TIPO_CONTRATO.MEDICO) {
      setModelos([...optionsMedicoModelo, ...optionsClinicaModelo]);
    } else if (conTipo === TIPO_CONTRATO.NOVO_CHIP) {
      setModelos([
        ...optionsChipModelo,
        ...optionsPacienteModelo,
        ...optionsClinicaModelo,
      ]);
    } else if (conTipo === TIPO_CONTRATO.CANCELA_CHIP) {
      setModelos([
        ...optionsChipCancelaModelo,
        ...optionsPacienteModelo,
        ...optionsClinicaModelo,
      ]);
    } else {
      setModelos([
        ...optionsPacienteModelo,
        ...optionsDadosCartaoModelo,
        ...optionsClinicaModelo,
      ]);
    }
    loadDados();
    return () => setModelos(undefined);
  }, [conTipo, origem, addToast]);

  const handleSubmitModelo = useCallback(
    async dados => {
      try {
        setLoading(true);
        formRefModelo.current?.setErrors({});
        const schema = Yup.object().shape({
          ConTexto: Yup.string()
            .min(20, 'Mínimo 20 caracteres')
            .required('Nome é obrigatório'),
        });

        await schema.validate(dados, {
          abortEarly: false,
        });

        const dadosForm = {
          ...dados,
          ConDataAlteracao: contrato?.contrato.ConDataAlteracao,
        };

        await api.put<IContrato>(
          `/contratos/${contrato?.contrato.ConCodigo}`,
          dadosForm,
        );

        addToast({ title: 'Contrato salvo', type: 'success' });

        handleVoltarLista(true);
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRefModelo.current?.setErrors(errors);
          return;
        }

        const mensagem = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Erro ao salvar contrato',
          description: mensagem,
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast, contrato, handleVoltarLista],
  );

  return (
    <>
      {loading && <Loading />}

      <Row noGutters>
        <Col md={12}>
          <Button
            type="button"
            variant="outline-success"
            size="sm"
            onClick={() => handleVoltarLista(false)}
          >
            <FaReply size={16} /> Voltar
          </Button>
          <Form
            ref={formRefModelo}
            initialData={{
              ConTexto: htmlDecode(contrato?.contrato.ConTexto || ''),
            }}
            onSubmit={handleSubmitModelo}
          >
            <Row className="p-1">
              <Col md="9">
                <span>
                  <b>CONTRATO {contrato?.contrato.ConTitulo}</b>
                </span>
                <br />
                <small className="text-muted">
                  Ultima alteração realizada por{' '}
                  {contrato?.contrato.usuarioEditor.UsuNome} em{' '}
                  {contrato?.contrato.dataAlteracaoFormatada}
                </small>
                <br />

                {contrato && contrato.contrato.ConPermiteEdicao && (
                  <>
                    {modelos && (
                      <EditorDocumento
                        name="ConTexto"
                        height={450}
                        modelo={modelos}
                      />
                    )}
                    <Button type="submit" variant="success" className="mt-3">
                      Salvar dados
                    </Button>
                  </>
                )}
                {contrato && contrato.contrato.ConPermiteEdicao === false && (
                  <>
                    <h5 className="text-info my-2">
                      Contato não permite alteração
                    </h5>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: htmlDecode(contrato.contrato.ConTexto),
                      }}
                    />
                  </>
                )}
                <br />
                <table
                  className="table table-hover table-bordered table-sm mt-3"
                  style={{ fontSize: 'small' }}
                >
                  <tr>
                    <th colSpan={2}>Dados da Clínica</th>
                  </tr>

                  <tr>
                    <td>[NOME_CLINICA]</td>
                    <td> {contrato?.clinica.CliNome}</td>
                  </tr>
                  <tr>
                    <td>[CNPJ_CLINICA]</td>
                    <td> {contrato?.clinica.CliCNPJ}</td>
                  </tr>
                  <tr>
                    <td>[REPRESENTANTE_CLINICA]</td>
                    <td> {contrato?.clinica.CliRepresentante}</td>
                  </tr>
                  <tr>
                    <td>[TELEFONE_CLINICA]</td>
                    <td> {contrato?.clinica.CliTelefone}</td>
                  </tr>
                  <tr>
                    <td>[TELEFONE2_CLINICA]</td>
                    <td> {contrato?.clinica.CliTelefone2}</td>
                  </tr>
                  <tr>
                    <td>[CIDADE_CLINICA]</td>
                    <td> {contrato?.clinica.CliCidade}</td>
                  </tr>
                  <tr>
                    <td>[ESTADO_CLINICA]</td>
                    <td> {contrato?.clinica.CliUF}</td>
                  </tr>
                  <tr>
                    <td>[ENDERECO_COMPLETO_CLINICA]</td>
                    <td> {contrato?.clinica.CliEndereco}</td>
                  </tr>
                  <tr>
                    <td>[EMAIL_CLINICA]</td>
                    <td> {contrato?.clinica.CliEmail}</td>
                  </tr>
                </table>
              </Col>
              <Col md="3">
                <>
                  <h5>Campos Personalizados</h5>
                  <table
                    className="table table-hover table-bordered table-sm"
                    style={{ fontSize: 'small' }}
                  >
                    {tipo === 'TRATAMENTO' && origem === 1 && (
                      <>
                        <tr>
                          <td className="text-center bg-dark text-white">
                            <b>Tratamento</b>
                          </td>
                        </tr>
                        {optionsTratamentoModelo.map(op => (
                          <tr key={`ContratoCadastroTratamento-${op.text}`}>
                            <td>
                              <b>{op.text}</b> {op.label}
                            </td>
                          </tr>
                        ))}
                      </>
                    )}
                    {tipo === 'TRATAMENTO' && origem === 2 && (
                      <>
                        <tr>
                          <td className="text-center bg-dark text-white">
                            <b>Pacote</b>
                          </td>
                        </tr>
                        {optionsPacoteModelo.map(op => (
                          <tr key={`ContratoCadastroPacote-${op.text}`}>
                            <td>
                              <b>{op.text}</b> {op.label}
                            </td>
                          </tr>
                        ))}
                      </>
                    )}

                    {conTipo !== TIPO_CONTRATO.MEDICO &&
                      conTipo !== TIPO_CONTRATO.NOVO_CHIP &&
                      conTipo !== TIPO_CONTRATO.CANCELA_CHIP && (
                        <>
                          <tr>
                            <td className="text-center bg-dark text-white">
                              <b>Paciente</b>
                            </td>
                          </tr>
                          {optionsPacienteModelo.map(op => (
                            <tr key={`ContratoCadastroPaciente-${op.text}`}>
                              <td>
                                <b>{op.text}</b> {op.label}
                              </td>
                            </tr>
                          ))}
                          {origem !== 2 && (
                            <>
                              <tr>
                                <td className="text-center bg-dark text-white">
                                  <b>Dados Cartão</b>
                                </td>
                              </tr>
                              {optionsDadosCartaoModelo.map(op => (
                                <tr key={`ContratoCadastroCartao-${op.text}`}>
                                  <td>
                                    <b>{op.text}</b> {op.label}
                                  </td>
                                </tr>
                              ))}
                            </>
                          )}
                        </>
                      )}
                    {conTipo === TIPO_CONTRATO.MEDICO && (
                      <>
                        <tr>
                          <td className="text-center bg-dark text-white">
                            <b>Médico</b>
                          </td>
                        </tr>
                        {optionsMedicoModelo.map(op => (
                          <tr key={`ContratoCadastroMedico-${op.text}`}>
                            <td>
                              <b>{op.text}</b> {op.label}
                            </td>
                          </tr>
                        ))}
                      </>
                    )}
                    {/* {tipo !== 'MEDICO' && (
                      <>
                        <tr>
                          <td className="text-center bg-dark text-white">
                            <b>Paciente</b>
                          </td>
                        </tr> */}

                    {conTipo === TIPO_CONTRATO.NOVO_CHIP && (
                      <>
                        <tr>
                          <td className="text-center bg-dark text-white">
                            <b>Linhas telefone</b>
                          </td>
                        </tr>
                        {optionsChipModelo.map(op => (
                          <tr key={`ContratoCadastrochip-${op.text}`}>
                            <td>
                              <b>{op.text}</b> {op.label}
                            </td>
                          </tr>
                        ))}
                        <tr>
                          <td className="text-center bg-dark text-white">
                            <b>Paciente</b>
                          </td>
                        </tr>
                        {optionsPacienteModelo.map(op => (
                          <tr key={`ContratoCadastroPaciente-${op.text}`}>
                            <td>
                              <b>{op.text}</b> {op.label}
                            </td>
                          </tr>
                        ))}
                        {tipo !== 'TRATAMENTO' && (
                          <>
                            <tr>
                              <td className="text-center bg-dark text-white">
                                <b>Dados Cartão</b>
                              </td>
                            </tr>
                            {optionsDadosCartaoModelo.map(op => (
                              <tr key={`ContratoCadastroCartao-${op.text}`}>
                                <td>
                                  <b>{op.text}</b> {op.label}
                                </td>
                              </tr>
                            ))}
                          </>
                        )}
                      </>
                    )}

                    {conTipo === TIPO_CONTRATO.CANCELA_CHIP && (
                      <>
                        <tr>
                          <td className="text-center bg-dark text-white">
                            <b>Linha telefone</b>
                          </td>
                        </tr>
                        {optionsChipCancelaModelo.map(op => (
                          <tr key={`ContratoCancelachip-${op.text}`}>
                            <td>
                              <b>{op.text}</b> {op.label}
                            </td>
                          </tr>
                        ))}
                        <tr>
                          <td className="text-center bg-dark text-white">
                            <b>Paciente</b>
                          </td>
                        </tr>
                        {optionsPacienteModelo.map(op => (
                          <tr key={`ContratoCadastroPaciente-${op.text}`}>
                            <td>
                              <b>{op.text}</b> {op.label}
                            </td>
                          </tr>
                        ))}
                      </>
                    )}
                    {tipo === 'TRATAMENTO' && (
                      <>
                        {origem !== 2 && (
                          <>
                            <tr>
                              <td className="text-center bg-dark text-white">
                                <b>Profissional</b>
                              </td>
                            </tr>
                            {optionsProfissionalModelo.map(op => (
                              <tr
                                key={`ContratoCadastroProfissional-${op.text}`}
                              >
                                <td>
                                  <b>{op.text}</b> {op.label}
                                </td>
                              </tr>
                            ))}
                          </>
                        )}
                        <tr>
                          <td className="text-center bg-dark text-white">
                            <b>Responsável</b>
                          </td>
                        </tr>
                        {optionsResponsavelModelo.map(op => (
                          <tr key={`ContratoCadastroResponsavel-${op.text}`}>
                            <td>
                              <b>{op.text}</b> {op.label}
                            </td>
                          </tr>
                        ))}
                      </>
                    )}
                    <tr>
                      <td className="text-center bg-dark text-white">
                        <b>Clinica</b>
                      </td>
                    </tr>
                    {optionsClinicaModelo.map(op => (
                      <tr key={`ContratoCadastroClinica-${op.text}`}>
                        <td>
                          <b>{op.text}</b> {op.label}
                        </td>
                      </tr>
                    ))}
                  </table>
                </>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
    </>
  );
};

export default ContratoCadastro;
